import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { TransactionV2 } from "@rentablo/api/apiv1";
import { RangeSelectorControlProps, rangeControlChanged } from "@hooks";
import {
  MobileListHeader,
  MobileListSettingsButton,
  MonthGroupedList,
  PeriodSetting,
  SearchSetting,
  SettingsFieldGroup,
  SettingsSections,
  SortSelect,
  useDateAmountSortOptions,
  type DateAmountSortField,
} from "@components/MobileList";
import { AccountTransactionsExportButton } from "../AccountTransactionsExportButton";
import {
  AccountTransactionMobileRow,
  transactionTitle,
} from "./AccountTransactionMobileRow";
import { AccountTransactionSheet } from "./AccountTransactionSheet";

const transactionDate = (transaction: TransactionV2) =>
  transaction.valueDate ?? "";
const transactionId = (transaction: TransactionV2) => transaction.id;

interface Props {
  accountId: number;
  currency?: string;
  transactions: TransactionV2[];
  totalCount?: number;
  rangeProps: RangeSelectorControlProps;
  filter: string;
  onFilterChange: (value: string) => void;
  sortField?: string;
  sortDirection?: TableSortDirection;
  onSort: (field: string, direction: TableSortDirection) => void;
  isRefreshing: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  showEndOfList: boolean;
}

export const AccountProtocolMobileList = ({
  accountId,
  currency,
  transactions,
  totalCount,
  rangeProps,
  filter,
  onFilterChange,
  sortField = "valueDate",
  sortDirection = "DESC",
  onSort,
  isRefreshing,
  hasMore,
  isLoadingMore,
  onLoadMore,
  showEndOfList,
}: Props) => {
  const { t } = useTranslation();
  const sortOptions = useDateAmountSortOptions();
  const [openTransaction, setOpenTransaction] = useState<TransactionV2>();
  const field: DateAmountSortField =
    sortField === "amount" ? "amount" : "valueDate";
  const isSortChanged = field !== "valueDate" || sortDirection !== "DESC";
  const hasChanges =
    rangeControlChanged(rangeProps) || filter !== "" || isSortChanged;

  const renderTransaction = useCallback(
    (transaction: TransactionV2) => (
      <AccountTransactionMobileRow
        transaction={transaction}
        currency={currency}
        onClick={setOpenTransaction}
      />
    ),
    [currency]
  );

  return (
    <Box
      data-testid="account-protocol-mobile-list"
      aria-busy={isRefreshing}
      sx={{ opacity: isRefreshing ? 0.6 : 1 }}
    >
      <MobileListHeader
        count={
          totalCount !== undefined &&
          t("singleAccountPage.protocolTable.mobile.count", {
            count: totalCount,
          })
        }
      >
        {transactions.length > 0 && (
          <AccountTransactionsExportButton
            accountId={accountId}
            range={rangeProps.range}
            currency={currency}
            order={[`${field} ${sortDirection.toLowerCase()}`]}
            compact
          />
        )}
        <MobileListSettingsButton
          hasChanges={hasChanges}
          canReset={hasChanges}
          onReset={() => {
            rangeProps.selectInterval(rangeProps.defaultIntervalType);
            onFilterChange("");
            onSort("valueDate", "DESC");
          }}
        >
          <SettingsSections testId="account-protocol-settings">
            <SettingsFieldGroup>
              <PeriodSetting rangeProps={rangeProps} />
              <SearchSetting
                label={t("singleAccountPage.protocolTable.mobile.search")}
                placeholder={t(
                  "singleAccountPage.protocolTable.search.placeholder"
                )}
                value={filter}
                onChange={onFilterChange}
              />
              <SortSelect
                label={t("domains.investments.investmentTable.mobile.sortBy")}
                options={sortOptions}
                field={field}
                direction={sortDirection}
                onChange={onSort}
              />
            </SettingsFieldGroup>
          </SettingsSections>
        </MobileListSettingsButton>
      </MobileListHeader>

      <MonthGroupedList
        items={transactions}
        getDate={transactionDate}
        getId={transactionId}
        renderItem={renderTransaction}
        groupByMonth={field === "valueDate"}
        emptyText={t("singleAccountPage.protocolTable.noItems")}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={onLoadMore}
        loadingMoreText={t(
          "singleAccountPage.protocolTable.mobile.loadingMore"
        )}
        endText={
          showEndOfList && t("singleAccountPage.protocolTable.mobile.allLoaded")
        }
        withLeading={false}
      />

      <AccountTransactionSheet
        transaction={openTransaction}
        title={openTransaction && transactionTitle(openTransaction, t)}
        currency={currency}
        onClose={() => setOpenTransaction(undefined)}
      />
    </Box>
  );
};
